import * as i0 from '@angular/core';
import { inject, NgZone, signal, Injectable, DestroyRef, input, viewChild, afterNextRender, Component, ChangeDetectionStrategy, Directive, output, forwardRef, model, ElementRef, afterRender, ViewEncapsulation, contentChild, TemplateRef, NgModule } from '@angular/core';
import { Marker, Popup, Map, AttributionControl, FullscreenControl, GeolocateControl, NavigationControl, ScaleControl, TerrainControl } from 'maplibre-gl';
import { AsyncSubject, Subject, fromEvent, Subscription, firstValueFrom, merge, tap as tap$1 } from 'rxjs';
import { takeUntilDestroyed, outputToObservable } from '@angular/core/rxjs-interop';
import { tap, switchMap, filter, debounceTime, takeUntil, take, startWith, map } from 'rxjs/operators';
import { NgTemplateOutlet } from '@angular/common';
const _c0 = ["content"];
const _c1 = ["*"];
const _c2 = ["container"];
const _c3 = () => ({
  "circle-radius": 0
});
const _c4 = a0 => ({
  $implicit: a0
});
function MarkersForClustersComponent_For_2_Conditional_0_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function MarkersForClustersComponent_For_2_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "mgl-marker", 1);
    i0.ɵɵtemplate(1, MarkersForClustersComponent_For_2_Conditional_0_ng_container_1_Template, 1, 0, "ng-container", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const feature_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("feature", feature_r1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.clusterPointTpl())("ngTemplateOutletContext", i0.ɵɵpureFunction1(3, _c4, feature_r1));
  }
}
function MarkersForClustersComponent_For_2_Conditional_1_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function MarkersForClustersComponent_For_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "mgl-marker", 1);
    i0.ɵɵtemplate(1, MarkersForClustersComponent_For_2_Conditional_1_ng_container_1_Template, 1, 0, "ng-container", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const feature_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("feature", feature_r1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.pointTpl())("ngTemplateOutletContext", i0.ɵɵpureFunction1(3, _c4, feature_r1));
  }
}
function MarkersForClustersComponent_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, MarkersForClustersComponent_For_2_Conditional_0_Template, 2, 5, "mgl-marker", 1)(1, MarkersForClustersComponent_For_2_Conditional_1_Template, 2, 5, "mgl-marker", 1);
  }
  if (rf & 2) {
    const feature_r1 = ctx.$implicit;
    i0.ɵɵconditional(feature_r1.properties.cluster ? 0 : 1);
  }
}
const keepAvailableObjectValues = object => {
  return Object.keys(object).reduce((acc, curr) => {
    const tKey = curr;
    if (object[tKey] === undefined) {
      return acc;
    }
    return {
      ...acc,
      [tKey]: object[tKey]
    };
  }, {});
};
let MapService = /*#__PURE__*/(() => {
  class MapService {
    constructor() {
      this.zone = inject(NgZone);
      this.mapCreated = new AsyncSubject();
      this.mapLoaded = new AsyncSubject();
      this.markersToRemove = signal([]);
      this.popupsToRemove = signal([]);
      this.imageIdsToRemove = signal([]);
      this.mapCreated$ = this.mapCreated.asObservable();
      this.mapLoaded$ = this.mapLoaded.asObservable();
    }
    setup(options) {
      // Workaround rollup issue
      this.createMap(options.mapOptions);
      this.hookEvents(options.mapEvents);
      this.mapEvents = options.mapEvents;
      this.mapCreated.next(undefined);
      this.mapCreated.complete();
      if (options.mapOptions.terrain) {
        this.mapInstance.on('load', () => {
          this.updateTerrain(options.mapOptions.terrain);
        });
      }
    }
    destroyMap() {
      if (this.mapInstance) {
        this.mapInstance.remove();
      }
    }
    updateMinZoom(minZoom) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setMinZoom(minZoom);
      });
    }
    updateMaxZoom(maxZoom) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setMaxZoom(maxZoom);
      });
    }
    updateMinPitch(minPitch) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setMinPitch(minPitch);
      });
    }
    updateMaxPitch(maxPitch) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setMaxPitch(maxPitch);
      });
    }
    updateRenderWorldCopies(status) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setRenderWorldCopies(status);
      });
    }
    updateScrollZoom(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.scrollZoom.enable() : this.mapInstance.scrollZoom.disable();
      });
    }
    updateDragRotate(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.dragRotate.enable() : this.mapInstance.dragRotate.disable();
      });
    }
    updateTouchPitch(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.touchPitch.enable() : this.mapInstance.touchPitch.disable();
      });
    }
    updateTouchZoomRotate(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.touchZoomRotate.enable() : this.mapInstance.touchZoomRotate.disable();
      });
    }
    updateDoubleClickZoom(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.doubleClickZoom.enable() : this.mapInstance.doubleClickZoom.disable();
      });
    }
    updateKeyboard(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.keyboard.enable() : this.mapInstance.keyboard.disable();
      });
    }
    updateDragPan(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.dragPan.enable() : this.mapInstance.dragPan.disable();
      });
    }
    updateBoxZoom(status) {
      return this.zone.runOutsideAngular(() => {
        status ? this.mapInstance.boxZoom.enable() : this.mapInstance.boxZoom.disable();
      });
    }
    updateStyle(style) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setStyle(style);
      });
    }
    updateMaxBounds(maxBounds) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setMaxBounds(maxBounds);
      });
    }
    updateTerrain(options) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setTerrain(options);
      });
    }
    getTerrain() {
      return this.zone.runOutsideAngular(() => {
        return this.mapInstance.getTerrain();
      });
    }
    changeCanvasCursor(cursor) {
      const canvas = this.mapInstance.getCanvasContainer();
      canvas.style.cursor = cursor;
    }
    queryRenderedFeatures(pointOrBox, parameters) {
      return this.mapInstance.queryRenderedFeatures(pointOrBox, parameters);
    }
    panTo(center, options) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.panTo(center, options);
      });
    }
    move(movingMethod, movingOptions, zoom, center, bearing, pitch) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance[movingMethod]({
          ...movingOptions,
          zoom: zoom != null ? zoom : this.mapInstance.getZoom(),
          center: center != null ? center : this.mapInstance.getCenter(),
          bearing: bearing != null ? bearing : this.mapInstance.getBearing(),
          pitch: pitch != null ? pitch : this.mapInstance.getPitch()
        });
      });
    }
    addLayer(layer, bindEvents, before) {
      this.zone.runOutsideAngular(() => {
        Object.keys(layer.layerOptions).forEach(key => {
          const tkey = key;
          if (layer.layerOptions[tkey] === undefined) {
            delete layer.layerOptions[tkey];
          }
        });
        this.mapInstance.addLayer(layer.layerOptions, before);
        if (bindEvents) {
          this.mapInstance.on('click', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerClick.emit(evt);
            });
          });
          this.mapInstance.on('dblclick', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerDblClick.emit(evt);
            });
          });
          this.mapInstance.on('mousedown', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseDown.emit(evt);
            });
          });
          this.mapInstance.on('mouseup', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseUp.emit(evt);
            });
          });
          this.mapInstance.on('mouseenter', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseEnter.emit(evt);
            });
          });
          this.mapInstance.on('mouseleave', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseLeave.emit(evt);
            });
          });
          this.mapInstance.on('mousemove', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseMove.emit(evt);
            });
          });
          this.mapInstance.on('mouseover', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseOver.emit(evt);
            });
          });
          this.mapInstance.on('mouseout', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerMouseOut.emit(evt);
            });
          });
          this.mapInstance.on('contextmenu', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerContextMenu.emit(evt);
            });
          });
          this.mapInstance.on('touchstart', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerTouchStart.emit(evt);
            });
          });
          this.mapInstance.on('touchend', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerTouchEnd.emit(evt);
            });
          });
          this.mapInstance.on('touchcancel', layer.layerOptions.id, evt => {
            this.zone.run(() => {
              layer.layerEvents.layerTouchCancel.emit(evt);
            });
          });
        }
      });
    }
    removeLayer(layerId) {
      this.zone.runOutsideAngular(() => {
        if (this.mapInstance.getLayer(layerId) != null) {
          this.mapInstance.removeLayer(layerId);
        }
      });
    }
    addMarker(marker) {
      const options = {
        offset: marker.markersOptions.offset,
        anchor: marker.markersOptions.anchor,
        color: marker.markersOptions.color,
        scale: marker.markersOptions.scale,
        draggable: !!marker.markersOptions.draggable,
        rotationAlignment: marker.markersOptions.rotationAlignment,
        rotation: marker.markersOptions.rotation,
        pitchAlignment: marker.markersOptions.pitchAlignment,
        clickTolerance: marker.markersOptions.clickTolerance,
        element: marker.markersOptions.element.childNodes.length > 0 ? marker.markersOptions.element : undefined,
        opacity: marker.markersOptions.opacity,
        opacityWhenCovered: marker.markersOptions.opacityWhenCovered,
        subpixelPositioning: marker.markersOptions.subpixelPositioning
      };
      const markerInstance = new Marker(options);
      markerInstance.on('dragstart', event => {
        if (event) {
          const {
            target
          } = event;
          this.zone.run(() => {
            marker.markersEvents.markerDragStart.emit(target);
          });
        }
      });
      /*
            */
      markerInstance.on('drag', event => {
        if (event) {
          const {
            target
          } = event;
          this.zone.run(() => marker.markersEvents.markerDrag.emit(target));
        }
      });
      markerInstance.on('dragend', event => {
        if (event) {
          const {
            target
          } = event;
          this.zone.run(() => marker.markersEvents.markerDragEnd.emit(target));
        }
      });
      const lngLat = marker.markersOptions.feature ? marker.markersOptions.feature.geometry.coordinates : marker.markersOptions.lngLat;
      markerInstance.setLngLat(lngLat);
      return this.zone.runOutsideAngular(() => {
        markerInstance.addTo(this.mapInstance);
        return markerInstance;
      });
    }
    removeMarker(marker) {
      this.markersToRemove.update(markers => [...markers, marker]);
    }
    createPopup(popup, element) {
      return this.zone.runOutsideAngular(() => {
        const popupOptions = keepAvailableObjectValues(popup.popupOptions);
        const popupInstance = new Popup(popupOptions);
        popupInstance.setDOMContent(element);
        popupInstance.on('close', () => {
          this.zone.run(() => {
            popup.popupEvents.popupClose.emit();
          });
        });
        popupInstance.on('open', () => {
          this.zone.run(() => {
            popup.popupEvents.popupOpen.emit();
          });
        });
        return popupInstance;
      });
    }
    addPopupToMap(popup, lngLat, skipOpenEvent = false) {
      return this.zone.runOutsideAngular(() => {
        if (skipOpenEvent && popup._listeners) {
          delete popup._listeners['open'];
        }
        popup.setLngLat(lngLat);
        popup.addTo(this.mapInstance);
      });
    }
    addPopupToMarker(marker, popup) {
      return this.zone.runOutsideAngular(() => {
        marker.setPopup(popup);
      });
    }
    removePopupFromMap(popup, skipCloseEvent = false) {
      if (skipCloseEvent && popup._listeners) {
        delete popup._listeners['close'];
      }
      this.popupsToRemove.update(popups => [...popups, popup]);
    }
    removePopupFromMarker(marker) {
      return this.zone.runOutsideAngular(() => {
        marker.setPopup(undefined);
      });
    }
    addControl(control, position) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.addControl(control, position);
      });
    }
    removeControl(control) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.removeControl(control);
      });
    }
    async loadAndAddImage(imageId, url, options) {
      return this.zone.runOutsideAngular(async () => {
        const image = await this.mapInstance.loadImage(url);
        this.addImage(imageId, image.data, options);
      });
    }
    addImage(imageId, data, options) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.addImage(imageId, data, options);
      });
    }
    removeImage(imageId) {
      this.imageIdsToRemove.update(imagesIds => [...imagesIds, imageId]);
    }
    addSource(sourceId, source) {
      return this.zone.runOutsideAngular(() => {
        Object.keys(source).forEach(key => source[key] === undefined && delete source[key]);
        this.mapInstance.addSource(sourceId, source);
      });
    }
    getSource(sourceId) {
      return this.mapInstance.getSource(sourceId);
    }
    removeSource(sourceId) {
      this.zone.runOutsideAngular(() => {
        this.findLayersBySourceId(sourceId).forEach(layer => this.mapInstance.removeLayer(layer.id));
        this.mapInstance.removeSource(sourceId);
      });
    }
    setAllLayerPaintProperty(layerId, paint) {
      return this.zone.runOutsideAngular(() => {
        Object.keys(paint).forEach(key => {
          // TODO Check for perf, setPaintProperty only on changed paint props maybe
          this.mapInstance.setPaintProperty(layerId, key, paint[key]);
        });
      });
    }
    setAllLayerLayoutProperty(layerId, layout) {
      return this.zone.runOutsideAngular(() => {
        Object.keys(layout).forEach(key => {
          // TODO Check for perf, setPaintProperty only on changed paint props maybe
          this.mapInstance.setLayoutProperty(layerId, key, layout[key]);
        });
      });
    }
    setLayerFilter(layerId, filter) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setFilter(layerId, filter);
      });
    }
    setLayerBefore(layerId, beforeId) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.moveLayer(layerId, beforeId);
      });
    }
    setLayerZoomRange(layerId, minZoom, maxZoom) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.setLayerZoomRange(layerId, minZoom ? minZoom : 0, maxZoom ? maxZoom : 20);
      });
    }
    fitBounds(bounds, options) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.fitBounds(bounds, options);
      });
    }
    fitScreenCoordinates(points, bearing, options) {
      return this.zone.runOutsideAngular(() => {
        this.mapInstance.fitScreenCoordinates(points[0], points[1], bearing, options);
      });
    }
    clearMapElements() {
      this.zone.runOutsideAngular(() => {
        this.removeMarkers();
        this.removePopups();
        this.removeImages();
      });
    }
    createMap(options) {
      NgZone.assertNotInAngularZone();
      const mapOptions = keepAvailableObjectValues(options);
      this.mapInstance = new Map(mapOptions);
      const isIEorEdge = window && /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
      if (isIEorEdge) {
        this.mapInstance.setStyle(options.style);
      }
    }
    removeMarkers() {
      for (const marker of this.markersToRemove()) {
        marker.remove();
      }
      this.markersToRemove.set([]);
    }
    removePopups() {
      for (const popup of this.popupsToRemove()) {
        popup.remove();
      }
      this.popupsToRemove.set([]);
    }
    removeImages() {
      for (const imageId of this.imageIdsToRemove()) {
        this.mapInstance.removeImage(imageId);
      }
      this.imageIdsToRemove.set([]);
    }
    findLayersBySourceId(sourceId) {
      const layers = this.mapInstance.getStyle().layers;
      if (layers == null) {
        return [];
      }
      return layers.filter(l => 'source' in l ? l.source === sourceId : false);
    }
    hookEvents(events) {
      this.mapInstance.on('load', evt => {
        this.mapLoaded.next(undefined);
        this.mapLoaded.complete();
        this.zone.run(() => {
          events.mapLoad.emit(evt.target);
        });
      });
      this.mapInstance.on('resize', evt => this.zone.run(() => {
        events.mapResize.emit(evt);
      }));
      this.mapInstance.on('remove', evt => this.zone.run(() => {
        events.mapRemove.emit(evt);
      }));
      this.mapInstance.on('mousedown', evt => this.zone.run(() => {
        events.mapMouseDown.emit(evt);
      }));
      this.mapInstance.on('mouseup', evt => this.zone.run(() => {
        events.mapMouseUp.emit(evt);
      }));
      this.mapInstance.on('mousemove', evt => this.zone.run(() => {
        events.mapMouseMove.emit(evt);
      }));
      this.mapInstance.on('click', evt => this.zone.run(() => {
        events.mapClick.emit(evt);
      }));
      this.mapInstance.on('dblclick', evt => this.zone.run(() => {
        events.mapDblClick.emit(evt);
      }));
      this.mapInstance.on('mouseover', evt => this.zone.run(() => {
        events.mapMouseOver.emit(evt);
      }));
      this.mapInstance.on('mouseout', evt => this.zone.run(() => {
        events.mapMouseOut.emit(evt);
      }));
      this.mapInstance.on('contextmenu', evt => this.zone.run(() => {
        events.mapContextMenu.emit(evt);
      }));
      this.mapInstance.on('touchstart', evt => this.zone.run(() => {
        events.mapTouchStart.emit(evt);
      }));
      this.mapInstance.on('touchend', evt => this.zone.run(() => {
        events.mapTouchEnd.emit(evt);
      }));
      this.mapInstance.on('touchmove', evt => this.zone.run(() => {
        events.mapTouchMove.emit(evt);
      }));
      this.mapInstance.on('touchcancel', evt => this.zone.run(() => {
        events.mapTouchCancel.emit(evt);
      }));
      this.mapInstance.on('wheel', evt => this.zone.run(() => {
        events.mapWheel.emit(evt);
      }));
      this.mapInstance.on('movestart', evt => this.zone.run(() => events.moveStart.emit(evt)));
      this.mapInstance.on('move', evt => this.zone.run(() => events.move.emit(evt)));
      this.mapInstance.on('moveend', evt => this.zone.run(() => events.moveEnd.emit(evt)));
      this.mapInstance.on('dragstart', evt => this.zone.run(() => {
        events.mapDragStart.emit(evt);
      }));
      this.mapInstance.on('drag', evt => this.zone.run(() => {
        events.mapDrag.emit(evt);
      }));
      this.mapInstance.on('dragend', evt => this.zone.run(() => {
        events.mapDragEnd.emit(evt);
      }));
      this.mapInstance.on('zoomstart', evt => this.zone.run(() => events.zoomStart.emit(evt)));
      this.mapInstance.on('zoom', evt => this.zone.run(() => events.zoomEvt.emit(evt)));
      this.mapInstance.on('zoomend', evt => this.zone.run(() => events.zoomEnd.emit(evt)));
      this.mapInstance.on('rotatestart', evt => this.zone.run(() => events.rotateStart.emit(evt)));
      this.mapInstance.on('rotate', evt => this.zone.run(() => events.rotate.emit(evt)));
      this.mapInstance.on('rotateend', evt => this.zone.run(() => events.rotateEnd.emit(evt)));
      this.mapInstance.on('pitchstart', evt => this.zone.run(() => events.pitchStart.emit(evt)));
      this.mapInstance.on('pitch', evt => this.zone.run(() => events.pitchEvt.emit(evt)));
      this.mapInstance.on('pitchend', evt => this.zone.run(() => events.pitchEnd.emit(evt)));
      this.mapInstance.on('boxzoomstart', evt => this.zone.run(() => events.boxZoomStart.emit(evt)));
      this.mapInstance.on('boxzoomend', evt => this.zone.run(() => events.boxZoomEnd.emit(evt)));
      this.mapInstance.on('boxzoomcancel', evt => this.zone.run(() => events.boxZoomCancel.emit(evt)));
      this.mapInstance.on('webglcontextlost', evt => this.zone.run(() => events.webGlContextLost.emit(evt)));
      this.mapInstance.on('webglcontextrestored', evt => this.zone.run(() => events.webGlContextRestored.emit(evt)));
      this.mapInstance.on('render', evt => this.zone.run(() => events.render.emit(evt)));
      this.mapInstance.on('error', evt => this.zone.run(() => {
        events.mapError.emit(evt);
      }));
      this.mapInstance.on('data', evt => this.zone.run(() => events.data.emit(evt)));
      this.mapInstance.on('styledata', evt => this.zone.run(() => events.styleData.emit(evt)));
      this.mapInstance.on('sourcedata', evt => this.zone.run(() => events.sourceData.emit(evt)));
      this.mapInstance.on('dataloading', evt => this.zone.run(() => events.dataLoading.emit(evt)));
      this.mapInstance.on('styledataloading', evt => this.zone.run(() => events.styleDataLoading.emit(evt)));
      this.mapInstance.on('sourcedataloading', evt => this.zone.run(() => events.sourceDataLoading.emit(evt)));
      this.mapInstance.on('styleimagemissing', evt => this.zone.run(() => events.styleImageMissing.emit(evt)));
      this.mapInstance.on('idle', evt => this.zone.run(() => events.idle.emit(evt)));
    }
    static {
      this.ɵfac = function MapService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MapService,
        factory: MapService.ɵfac
      });
    }
  }
  return MapService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class CustomControl {
  constructor(container) {
    this.container = container;
  }
  /** @inheritdoc */
  onAdd() {
    return this.container;
  }
  /** @inheritdoc */
  onRemove() {
    return this.container.parentNode.removeChild(this.container);
  }
  /** @inheritdoc */
  getDefaultPosition() {
    return 'top-right';
  }
}
/**
 * `mgl-control` - a custom control component
 * @see [Controls](https://maplibre.org/maplibre-gl-js/docs/API/interfaces/IControl/)
 *
 * @category Components
 *
 * @example
 * ```html
 * ...
 * <mgl-map ...>
 *   <mgl-control> Hello </mgl-control>
 *   ...
 *   <mgl-control mglNavigation></mgl-control>
 *   ...
 *   <mgl-control mglScale unit="imperial" position="top-right"></mgl-control>
 *   ...
 *   <mgl-control
 *     mglTerrain
 *     source="rasterDemSource"
 *     exaggeration="3.1"
 *   ></mgl-control>
 * </mgl-map>
 * ```
 */
let ControlComponent = /*#__PURE__*/(() => {
  class ControlComponent {
    constructor() {
      /** Init injection */
      this.mapService = inject(MapService);
      this.destroyRef = inject(DestroyRef);
      /** Init input */
      this.position = input();
      /** @hidden */
      this.content = viewChild.required('content');
      afterNextRender(() => {
        if (this.content().nativeElement.childNodes.length) {
          this.control = new CustomControl(this.content().nativeElement);
          this.mapService.mapCreated$.subscribe(() => {
            this.mapService.addControl(this.control, this.position());
          });
        }
      });
      this.destroyRef.onDestroy(() => {
        if (this.mapService?.mapInstance?.hasControl(this.control)) {
          this.mapService.removeControl(this.control);
        }
      });
    }
    static {
      this.ɵfac = function ControlComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ControlComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: ControlComponent,
        selectors: [["mgl-control"]],
        viewQuery: function ControlComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuerySignal(ctx.content, _c0, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        inputs: {
          position: [1, "position"]
        },
        ngContentSelectors: _c1,
        decls: 3,
        vars: 0,
        consts: [["content", ""], [1, "maplibregl-ctrl"]],
        template: function ControlComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 1, 0);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return ControlComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglAttribution` - an attribution control directive
 *
 * @category Directives
 *
 * @see [Add custom attribution](https://maplibre.org/ngx-maplibre-gl/demo/custom-attribution)
 * @see [AttributionControl](https://maplibre.org/maplibre-gl-js/docs/API/classes/AttributionControl)
 */
let AttributionControlDirective = /*#__PURE__*/(() => {
  class AttributionControlDirective {
    constructor() {
      /* Init injection */
      this.mapService = inject(MapService);
      this.controlComponent = inject(ControlComponent, {
        host: true
      });
      /** Init input */
      this.compact = input();
      /** Init input */
      this.customAttribution = input();
      afterNextRender(() => {
        this.mapService.mapCreated$.subscribe(() => {
          if (this.controlComponent.control) {
            throw new Error('Another control is already set for this control');
          }
          const options = keepAvailableObjectValues({
            compact: this.compact(),
            customAttribution: this.customAttribution()
          });
          this.controlComponent.control = new AttributionControl(options);
          this.mapService.addControl(this.controlComponent.control, this.controlComponent.position());
        });
      });
    }
    static {
      this.ɵfac = function AttributionControlDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AttributionControlDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AttributionControlDirective,
        selectors: [["", "mglAttribution", ""]],
        inputs: {
          compact: [1, "compact"],
          customAttribution: [1, "customAttribution"]
        }
      });
    }
  }
  return AttributionControlDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglFullscreen` - a fullscreen control directive
 *
 * @category Directives
 */
let FullscreenControlDirective = /*#__PURE__*/(() => {
  class FullscreenControlDirective {
    constructor() {
      /* Init injection */
      this.mapService = inject(MapService);
      this.controlComponent = inject(ControlComponent, {
        host: true
      });
      /* Init inputs */
      this.container = input();
      afterNextRender(() => {
        this.mapService.mapCreated$.subscribe(() => {
          if (this.controlComponent.control) {
            throw new Error('Another control is already set for this control');
          }
          this.controlComponent.control = new FullscreenControl({
            container: this.container()
          });
          this.mapService.addControl(this.controlComponent.control, this.controlComponent.position());
        });
      });
    }
    onFullscreen() {
      this.mapService.mapInstance.resize();
    }
    static {
      this.ɵfac = function FullscreenControlDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FullscreenControlDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FullscreenControlDirective,
        selectors: [["", "mglFullscreen", ""]],
        hostBindings: function FullscreenControlDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("webkitfullscreenchange", function FullscreenControlDirective_webkitfullscreenchange_HostBindingHandler() {
              return ctx.onFullscreen();
            }, false, i0.ɵɵresolveWindow);
          }
        },
        inputs: {
          container: [1, "container"]
        }
      });
    }
  }
  return FullscreenControlDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglGeolocate` - a geolocate control directive
 *
 * @category Directives
 *
 * @see [Locate user](https://maplibre.org/ngx-maplibre-gl/demo/locate-user)
 * @see [GeolocateControl](https://maplibre.org/maplibre-gl-js/docs/API/classes/GeolocateControl)
 */
let GeolocateControlDirective = /*#__PURE__*/(() => {
  class GeolocateControlDirective {
    constructor() {
      /* Init injection */
      this.mapService = inject(MapService);
      this.controlComponent = inject(ControlComponent, {
        host: true
      });
      /* Init inputs */
      this.positionOptions = input();
      /* Init inputs */
      this.fitBoundsOptions = input();
      /* Init inputs */
      this.trackUserLocation = input();
      /* Init inputs */
      this.showUserLocation = input();
      this.geolocate = output();
      afterNextRender(() => {
        this.mapService.mapCreated$.subscribe(() => {
          if (this.controlComponent.control) {
            throw new Error('Another control is already set for this control');
          }
          const options = keepAvailableObjectValues({
            positionOptions: this.positionOptions(),
            fitBoundsOptions: this.fitBoundsOptions(),
            trackUserLocation: this.trackUserLocation(),
            showUserLocation: this.showUserLocation()
          });
          this.controlComponent.control = new GeolocateControl(options);
          this.controlComponent.control.on('geolocate', data => {
            this.geolocate.emit(data);
          });
          this.mapService.addControl(this.controlComponent.control, this.controlComponent.position());
        });
      });
    }
    static {
      this.ɵfac = function GeolocateControlDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GeolocateControlDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GeolocateControlDirective,
        selectors: [["", "mglGeolocate", ""]],
        inputs: {
          positionOptions: [1, "positionOptions"],
          fitBoundsOptions: [1, "fitBoundsOptions"],
          trackUserLocation: [1, "trackUserLocation"],
          showUserLocation: [1, "showUserLocation"]
        },
        outputs: {
          geolocate: "geolocate"
        }
      });
    }
  }
  return GeolocateControlDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglNavigation` - a navigation control directive
 *
 * @category Directives
 *
 * @see [Navigation](https://maplibre.org/ngx-maplibre-gl/demo/navigation)
 * @see [NavigationControl](https://maplibre.org/maplibre-gl-js/docs/API/classes/NavigationControl)
 */
let NavigationControlDirective = /*#__PURE__*/(() => {
  class NavigationControlDirective {
    constructor() {
      /* Init injection */
      this.mapService = inject(MapService);
      this.controlComponent = inject(ControlComponent, {
        host: true
      });
      /* Init inputs */
      this.showCompass = input();
      /* Init inputs */
      this.showZoom = input();
      /* Init inputs */
      this.visualizePitch = input();
      afterNextRender(() => {
        this.mapService.mapCreated$.subscribe(() => {
          if (this.controlComponent.control) {
            throw new Error('Another control is already set for this control');
          }
          const options = keepAvailableObjectValues({
            showCompass: this.showCompass(),
            showZoom: this.showZoom(),
            visualizePitch: this.visualizePitch()
          });
          this.controlComponent.control = new NavigationControl(options);
          this.mapService.addControl(this.controlComponent.control, this.controlComponent.position());
        });
      });
    }
    static {
      this.ɵfac = function NavigationControlDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NavigationControlDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NavigationControlDirective,
        selectors: [["", "mglNavigation", ""]],
        inputs: {
          showCompass: [1, "showCompass"],
          showZoom: [1, "showZoom"],
          visualizePitch: [1, "visualizePitch"]
        }
      });
    }
  }
  return NavigationControlDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglScale` - a scale control directive
 *
 * @category Directives
 *
 * @see [Scale](https://maplibre.org/ngx-maplibre-gl/demo/ngx-scale-control)
 * @see [ScaleControl](https://maplibre.org/maplibre-gl-js/docs/API/classes/ScaleControl)
 */
let ScaleControlDirective = /*#__PURE__*/(() => {
  class ScaleControlDirective {
    constructor() {
      /* Init injection */
      this.mapService = inject(MapService);
      this.controlComponent = inject(ControlComponent, {
        host: true
      });
      /* Init inputs */
      this.maxWidth = input();
      /* Dynamic inputs */
      this.unit = input();
      afterNextRender(() => {
        this.mapService.mapCreated$.subscribe(() => {
          if (this.controlComponent.control) {
            throw new Error('Another control is already set for this control');
          }
          const options = keepAvailableObjectValues({
            maxWidth: this.maxWidth(),
            unit: this.unit()
          });
          this.controlComponent.control = new ScaleControl(options);
          this.mapService.addControl(this.controlComponent.control, this.controlComponent.position());
        });
      });
    }
    ngOnChanges(changes) {
      if (changes.unit && !changes.unit.isFirstChange()) {
        this.controlComponent.control.setUnit(changes.unit.currentValue);
      }
    }
    static {
      this.ɵfac = function ScaleControlDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ScaleControlDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ScaleControlDirective,
        selectors: [["", "mglScale", ""]],
        inputs: {
          maxWidth: [1, "maxWidth"],
          unit: [1, "unit"]
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return ScaleControlDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglTerrain` - a terrain control directive
 *
 * @category Directives
 *
 * @see [Terrain](https://maplibre.org/ngx-maplibre-gl/demo/terrain-control)
 * @see [TerrainControl](https://maplibre.org/maplibre-gl-js/docs/API/classes/TerrainControl)
 */
let TerrainControlDirective = /*#__PURE__*/(() => {
  class TerrainControlDirective {
    constructor() {
      /* Init injection */
      this.mapService = inject(MapService);
      this.controlComponent = inject(ControlComponent, {
        host: true
      });
      /* Init inputs */
      this.source = input.required();
      this.exaggeration = input();
      afterNextRender(() => {
        this.mapService.mapCreated$.subscribe(() => {
          if (this.controlComponent.control) {
            throw new Error('Another control is already set for this control');
          }
          const options = {
            source: this.source(),
            exaggeration: this.exaggeration() ?? 1
          };
          this.controlComponent.control = new TerrainControl(options);
          this.mapService.addControl(this.controlComponent.control, this.controlComponent.position());
        });
      });
    }
    static {
      this.ɵfac = function TerrainControlDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TerrainControlDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TerrainControlDirective,
        selectors: [["", "mglTerrain", ""]],
        inputs: {
          source: [1, "source"],
          exaggeration: [1, "exaggeration"]
        }
      });
    }
  }
  return TerrainControlDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 *
 * @category Source Components
 */
let SourceDirective = /*#__PURE__*/(() => {
  class SourceDirective {
    constructor() {
      /** Init injection */
      this.mapService = inject(MapService);
      this.destroyRef = inject(DestroyRef);
      /**  Init input */
      this.id = input.required();
      this.sourceId = signal(null);
      this.loadSourceSubject = new Subject();
      this.loadSource$ = this.loadSourceSubject.asObservable();
      this.destroyRef.onDestroy(() => this.removeSource.bind(this));
    }
    ngOnInit() {
      this.mapService.mapLoaded$.pipe(tap(() => this.loadSourceSubject.next()), switchMap(() => fromEvent(this.mapService.mapInstance, 'styledata').pipe(filter(() => !this.mapService.mapInstance.getSource(this.id())), tap(() => this.loadSourceSubject.next()))), takeUntilDestroyed(this.destroyRef)).subscribe();
    }
    refresh() {
      this.removeSource();
      this.loadSourceSubject.next();
    }
    removeSource() {
      if (this.sourceId()) {
        this.mapService.removeSource(this.id());
        this.sourceId.set(null);
      }
    }
    addSource(source) {
      this.mapService.addSource(this.id(), source);
      this.sourceId.set(this.id());
    }
    getSource() {
      return this.mapService.getSource(this.id());
    }
    static {
      this.ɵfac = function SourceDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SourceDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: SourceDirective,
        inputs: {
          id: [1, "id"]
        }
      });
    }
  }
  return SourceDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-geojson-source` - a geojson source component
 * @see [geojson](https://maplibre.org/maplibre-gl-js/docs/API/classes/maplibregl.GeoJSONSource/)
 *
 * @category Source Components
 *
 * @example
 * ```html
 * ...
 * <mgl-map ...>
 *   <mgl-geojson-source id="symbols-source">
 *     <mgl-feature
 *       *ngFor="let geometry of geometries"
 *       [geometry]="geometry"
 *     ></mgl-feature>
 *   </mgl-geojson-source>
 *   ...
 *   <mgl-geojson-source
 *     id="earthquakes"
 *     [data]="earthquakes"
 *     [cluster]="true"
 *     [clusterMaxZoom]="14"
 *     [clusterRadius]="50"
 *   ></mgl-geojson-source>
 * </mgl-map>
 *
 * ```
 */
let GeoJSONSourceComponent = /*#__PURE__*/(() => {
  class GeoJSONSourceComponent {
    constructor() {
      /** Init injections */
      this.sourceDirective = inject(SourceDirective);
      /** Init injection */
      this.ngZone = inject(NgZone);
      /** Dynamic input */
      this.data = input({
        type: 'FeatureCollection',
        features: []
      });
      /** Dynamic input */
      this.maxzoom = input();
      /** Dynamic input */
      this.attribution = input();
      /** Dynamic input */
      this.buffer = input();
      /** Dynamic input */
      this.tolerance = input();
      /** Dynamic input */
      this.cluster = input();
      /** Dynamic input */
      this.clusterRadius = input();
      /** Dynamic input */
      this.clusterMaxZoom = input();
      /** Dynamic input */
      this.clusterMinPoints = input();
      /** Dynamic input */
      this.clusterProperties = input();
      /** Dynamic input */
      this.lineMetrics = input();
      /** Dynamic input */
      this.generateId = input();
      /** Dynamic input */
      this.promoteId = input();
      /** Dynamic input */
      this.filter = input();
      this.updateFeatureDataSubject = new Subject();
      this.featureIdCounter = signal(0);
      this.sourceDirective.loadSource$.pipe(tap(() => this.sourceDirective.addSource(this.getGeoJSONSourceSpecification())), switchMap(() => this.updateFeature()), takeUntilDestroyed()).subscribe();
    }
    ngOnChanges(changes) {
      if (!this.sourceDirective.sourceId()) {
        return;
      }
      if (changes.maxzoom && !changes.maxzoom.isFirstChange() || changes.attribution && !changes.attribution.isFirstChange() || changes.buffer && !changes.buffer.isFirstChange() || changes.tolerance && !changes.tolerance.isFirstChange() || changes.cluster && !changes.cluster.isFirstChange() || changes.clusterRadius && !changes.clusterRadius.isFirstChange() || changes.clusterMaxZoom && !changes.clusterMaxZoom.isFirstChange() || changes.clusterMinPoints && !changes.clusterMinPoints.isFirstChange() || changes.clusterProperties && !changes.clusterProperties.isFirstChange() || changes.lineMetrics && !changes.lineMetrics.isFirstChange() || changes.generateId && !changes.generateId.isFirstChange() || changes.promoteId && !changes.promoteId.isFirstChange() || changes.filter && !changes.filter.isFirstChange()) {
        this.sourceDirective.refresh();
      }
      if (changes.data && !changes.data.isFirstChange()) {
        const source = this.sourceDirective.getSource();
        if (source === undefined) {
          return;
        }
        source.setData(changes.data.currentValue);
      }
    }
    /**
     * For clustered sources, fetches the zoom at which the given cluster expands.
     * @param clusterId The value of the cluster's cluster_id property.
     */
    async getClusterExpansionZoom(clusterId) {
      const source = this.sourceDirective.getSource();
      return this.ngZone.run(async () => {
        return source.getClusterExpansionZoom(clusterId);
      });
    }
    /**
     * For clustered sources, fetches the children of the given cluster on the next zoom level (as an array of GeoJSON features).
     * @param clusterId The value of the cluster's cluster_id property.
     */
    async getClusterChildren(clusterId) {
      const source = this.sourceDirective.getSource();
      return this.ngZone.run(async () => {
        return source.getClusterChildren(clusterId);
      });
    }
    /**
     * For clustered sources, fetches the original points that belong to the cluster (as an array of GeoJSON features).
     * @param clusterId The value of the cluster's cluster_id property.
     * @param limit The maximum number of features to return.
     * @param offset The number of features to skip (e.g. for pagination).
     */
    async getClusterLeaves(clusterId, limit, offset) {
      const source = this.sourceDirective.getSource();
      return this.ngZone.run(async () => {
        return source.getClusterLeaves(clusterId, limit, offset);
      });
    }
    _addFeature(feature) {
      const collection = this.data();
      collection.features.push(feature);
      this.updateFeatureDataSubject.next();
    }
    _removeFeature(feature) {
      const collection = this.data();
      const index = collection.features.indexOf(feature);
      if (index > -1) {
        collection.features.splice(index, 1);
      }
      this.updateFeatureDataSubject.next();
    }
    updateFeatureData() {
      this.updateFeatureDataSubject.next();
    }
    _getNewFeatureId() {
      this.featureIdCounter.update(featureIdCounter => ++featureIdCounter);
      return this.featureIdCounter();
    }
    getGeoJSONSourceSpecification() {
      return {
        type: 'geojson',
        data: this.data(),
        maxzoom: this.maxzoom(),
        attribution: this.attribution(),
        buffer: this.buffer(),
        tolerance: this.tolerance(),
        cluster: this.cluster(),
        clusterRadius: this.clusterRadius(),
        clusterMaxZoom: this.clusterMaxZoom(),
        clusterMinPoints: this.clusterMinPoints(),
        clusterProperties: this.clusterProperties(),
        lineMetrics: this.lineMetrics(),
        generateId: this.generateId(),
        promoteId: this.promoteId(),
        filter: this.filter()
      };
    }
    updateFeature() {
      return this.updateFeatureDataSubject.pipe(debounceTime(0)).pipe(tap(() => {
        const source = this.sourceDirective.getSource();
        if (source === undefined) {
          return;
        }
        source.setData(this.data());
      }));
    }
    static {
      this.ɵfac = function GeoJSONSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GeoJSONSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: GeoJSONSourceComponent,
        selectors: [["mgl-geojson-source"]],
        inputs: {
          data: [1, "data"],
          maxzoom: [1, "maxzoom"],
          attribution: [1, "attribution"],
          buffer: [1, "buffer"],
          tolerance: [1, "tolerance"],
          cluster: [1, "cluster"],
          clusterRadius: [1, "clusterRadius"],
          clusterMaxZoom: [1, "clusterMaxZoom"],
          clusterMinPoints: [1, "clusterMinPoints"],
          clusterProperties: [1, "clusterProperties"],
          lineMetrics: [1, "lineMetrics"],
          generateId: [1, "generateId"],
          promoteId: [1, "promoteId"],
          filter: [1, "filter"]
        },
        features: [i0.ɵɵHostDirectivesFeature([{
          directive: SourceDirective,
          inputs: ["id", "id"]
        }]), i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function GeoJSONSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return GeoJSONSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-feature` - a feature component
 * [ngx] inside {@link GeoJSONSourceComponent} only
 *
 * @category Source Components
 */
let FeatureComponent = /*#__PURE__*/(() => {
  class FeatureComponent {
    constructor() {
      /** Init injection */
      this.geoJSONSourceComponent = inject(forwardRef(() => GeoJSONSourceComponent));
      /** Init input */
      this.id = model();
      /** Init input */
      this.geometry = input.required();
      /** Init input */
      this.properties = input();
    }
    ngOnInit() {
      const id = this.id();
      if (!id) {
        this.id.set(this.geoJSONSourceComponent._getNewFeatureId());
      }
      const properties = this.properties();
      this.feature = {
        type: 'Feature',
        geometry: this.geometry(),
        properties: properties ?? {}
      };
      this.feature.id = this.id();
      this.geoJSONSourceComponent._addFeature(this.feature);
    }
    ngOnDestroy() {
      this.geoJSONSourceComponent._removeFeature(this.feature);
    }
    updateCoordinates(coordinates) {
      this.feature.geometry.coordinates = coordinates;
      this.geoJSONSourceComponent.updateFeatureData();
    }
    static {
      this.ɵfac = function FeatureComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeatureComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: FeatureComponent,
        selectors: [["mgl-feature"]],
        inputs: {
          id: [1, "id"],
          geometry: [1, "geometry"],
          properties: [1, "properties"]
        },
        outputs: {
          id: "idChange"
        },
        decls: 0,
        vars: 0,
        template: function FeatureComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return FeatureComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mglDraggable` - a directive for Feature or Marker
 *
 * @category Directives
 *
 * @see [Draggable Marker](https://maplibre.org/ng-maplibre-gl/demo/ngx-drag-a-point)
 */
let DraggableDirective = /*#__PURE__*/(() => {
  class DraggableDirective {
    constructor() {
      /** Init injection */
      this.mapService = inject(MapService);
      this.ngZone = inject(NgZone);
      this.featureComponent = inject(FeatureComponent, {
        host: true,
        optional: true
      });
      // eslint-disable-next-line @angular-eslint/no-input-rename
      this.layer = input(null, {
        alias: 'mglDraggable'
      });
      this.featureDragStart = output();
      this.featureDragEnd = output();
      this.featureDrag = output();
      this.sub = new Subscription();
    }
    ngOnInit() {
      let enter$;
      let leave$;
      let updateCoords;
      const layer = this.layer();
      if (this.featureComponent && layer) {
        enter$ = outputToObservable(layer.layerMouseEnter);
        leave$ = outputToObservable(layer.layerMouseLeave);
        updateCoords = this.featureComponent.updateCoordinates.bind(this.featureComponent);
        if (this.featureComponent.geometry().type !== 'Point') {
          throw new Error('mglDraggable only support point feature');
        }
      } else {
        throw new Error('mglDraggable can only be used on Feature (with a layer as input) or Marker');
      }
      this.handleDraggable(enter$, leave$, updateCoords);
    }
    ngOnDestroy() {
      this.sub.unsubscribe();
    }
    handleDraggable(enter$, leave$, updateCoords) {
      let moving = false;
      let inside = false;
      this.mapService.mapCreated$.subscribe(() => {
        const mouseUp$ = fromEvent(this.mapService.mapInstance, 'mouseup');
        const dragStart$ = enter$.pipe(filter(() => !moving), filter(evt => this.filterFeature(evt)), tap(() => {
          inside = true;
          this.mapService.changeCanvasCursor('move');
          this.mapService.updateDragPan(false);
        }), switchMap(() => fromEvent(this.mapService.mapInstance, 'mousedown').pipe(takeUntil(leave$))));
        const dragging$ = dragStart$.pipe(switchMap(() => fromEvent(this.mapService.mapInstance, 'mousemove').pipe(takeUntil(mouseUp$))));
        const dragEnd$ = dragStart$.pipe(switchMap(() => mouseUp$.pipe(take(1))));
        this.sub.add(dragStart$.subscribe(evt => {
          moving = true;
          this.ngZone.run(() => {
            this.featureDragStart.emit(evt);
          });
        }));
        this.sub.add(dragging$.subscribe(evt => {
          updateCoords([evt.lngLat.lng, evt.lngLat.lat]);
          this.ngZone.run(() => {
            this.featureDrag.emit(evt);
          });
        }));
        this.sub.add(dragEnd$.subscribe(evt => {
          moving = false;
          this.ngZone.run(() => {
            this.featureDragEnd.emit(evt);
          });
          if (!inside) {
            // It's possible to dragEnd outside the target (small input lag)
            this.mapService.changeCanvasCursor('');
            this.mapService.updateDragPan(true);
          }
        }));
        this.sub.add(leave$.pipe(tap(() => inside = false), filter(() => !moving)).subscribe(() => {
          this.mapService.changeCanvasCursor('');
          this.mapService.updateDragPan(true);
        }));
      });
    }
    filterFeature(evt) {
      const layer = this.layer();
      if (this.featureComponent && layer) {
        const feature = this.mapService.queryRenderedFeatures(evt.point, {
          layers: [layer.id()],
          filter: ['all', ['==', '$type', 'Point'], ['==', '$id', this.featureComponent.id()]]
        })[0];
        if (!feature) {
          return false;
        }
      }
      return true;
    }
    static {
      this.ɵfac = function DraggableDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DraggableDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DraggableDirective,
        selectors: [["", "mglDraggable", ""]],
        inputs: {
          layer: [1, "mglDraggable", "layer"]
        },
        outputs: {
          featureDragStart: "featureDragStart",
          featureDragEnd: "featureDragEnd",
          featureDrag: "featureDrag"
        }
      });
    }
  }
  return DraggableDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-image` - an image component
 * @see [addImage](https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/#addimage)
 *
 * @category Components
 *
 * @example
 * ```html
 * ...
 * <mgl-map
 *   ...
 * >
 *    <mgl-image
 *      id="image"
 *      url="https://..."
 *      (imageLoaded)="imageLoaded = true"
 *    >
 *    ...
 *    <mgl-image
 *      id="image2"
 *      [data]="{
 *        width: 64,
 *        height: 64,
 *        data: imageData
 *      }"
 *    >
 * </mgl-map>
 * ...
 * imageData: Uint8Array;
 * ```
 */
let ImageComponent = /*#__PURE__*/(() => {
  class ImageComponent {
    constructor() {
      /** Init injection */
      this.mapService = inject(MapService);
      this.destroyRef = inject(DestroyRef);
      this.zone = inject(NgZone);
      /** Init input */
      this.id = input.required();
      /** Dynamic input */
      this.data = input();
      /** Dynamic input */
      this.options = input();
      /** Dynamic input */
      this.url = input();
      this.imageError = output();
      this.imageLoaded = output();
      this.isAdded = signal(false);
      this.isAdding = signal(false);
      this.destroyRef.onDestroy(() => this.removeImage());
    }
    ngOnInit() {
      this.mapService.mapLoaded$.pipe(switchMap(() => fromEvent(this.mapService.mapInstance, 'styledata').pipe(startWith(undefined), filter(() => !this.isAdding() && !this.mapService.mapInstance.hasImage(this.id())))), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.addImage());
    }
    ngOnChanges(changes) {
      if (changes.data && !changes.data.isFirstChange() || changes.options && !changes.options.isFirstChange() || changes.url && !changes.url.isFirstChange()) {
        this.removeImage();
        this.ngOnInit();
      }
    }
    removeImage() {
      if (this.isAdded()) {
        this.mapService.removeImage(this.id());
      }
    }
    async addImage() {
      this.isAdding.set(true);
      const data = this.data();
      const url = this.url();
      if (data) {
        this.mapService.addImage(this.id(), data, this.options());
        this.isAdded.set(true);
        this.isAdding.set(false);
      } else if (url) {
        try {
          await this.mapService.loadAndAddImage(this.id(), url, this.options());
          this.isAdded.set(true);
          this.isAdding.set(false);
          this.zone.run(() => {
            this.imageLoaded.emit();
          });
        } catch (error) {
          this.zone.run(() => {
            this.imageError.emit(error);
          });
        }
      }
    }
    static {
      this.ɵfac = function ImageComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ImageComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: ImageComponent,
        selectors: [["mgl-image"]],
        inputs: {
          id: [1, "id"],
          data: [1, "data"],
          options: [1, "options"],
          url: [1, "url"]
        },
        outputs: {
          imageError: "imageError",
          imageLoaded: "imageLoaded"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function ImageComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return ImageComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-layer` - a layer component
 * @see [layers](https://maplibre.org/maplibre-style-spec/layers/)
 *
 * @category Layer Component
 *
 * @example
 * ```html
 * ...
 * <mgl-map ...>
 *   <mgl-layer
 *     id="state-borders"
 *     type="line"
 *     [source]="states"
 *     [paint]="{
 *       'line-color': '#627BC1',
 *       'line-width': 2
 *     }"
 *   ></mgl-layer>
 * </mgl-map>
 * ```
 */
let LayerComponent = /*#__PURE__*/(() => {
  class LayerComponent {
    constructor() {
      /** Init injection */
      this.destroyRef = inject(DestroyRef);
      this.mapService = inject(MapService);
      /** Init input */
      this.id = input.required();
      this.type = input.required();
      this.source = input();
      this.metadata = input();
      this.sourceLayer = input();
      /**
       * A flag to enable removeSource clean up functionality
       *
       * Init input
       */
      this.removeSource = input();
      this.filter = input();
      this.layout = input();
      this.paint = input();
      this.before = input();
      this.minzoom = input();
      this.maxzoom = input();
      this.layerClick = output();
      this.layerDblClick = output();
      this.layerMouseDown = output();
      this.layerMouseUp = output();
      this.layerMouseEnter = output();
      this.layerMouseLeave = output();
      this.layerMouseMove = output();
      this.layerMouseOver = output();
      this.layerMouseOut = output();
      this.layerContextMenu = output();
      this.layerTouchStart = output();
      this.layerTouchEnd = output();
      this.layerTouchCancel = output();
      this.layerAdded = signal(false);
      this.sourceIdAdded = signal(null);
    }
    ngOnInit() {
      this.mapService.mapLoaded$.pipe(switchMap(() => fromEvent(this.mapService.mapInstance, 'styledata').pipe(map(() => false), filter(() => !this.mapService.mapInstance.getLayer(this.id())), startWith(true))), takeUntilDestroyed(this.destroyRef)).subscribe(bindEvents => this.init(bindEvents));
    }
    ngOnChanges(changes) {
      if (!this.layerAdded()) {
        return;
      }
      if (changes.paint && !changes.paint.isFirstChange()) {
        this.mapService.setAllLayerPaintProperty(this.id(), changes.paint.currentValue);
      }
      if (changes.layout && !changes.layout.isFirstChange()) {
        this.mapService.setAllLayerLayoutProperty(this.id(), changes.layout.currentValue);
      }
      if (changes.filter && !changes.filter.isFirstChange()) {
        this.mapService.setLayerFilter(this.id(), changes.filter.currentValue);
      }
      if (changes.before && !changes.before.isFirstChange()) {
        this.mapService.setLayerBefore(this.id(), changes.before.currentValue);
      }
      if (changes.minzoom && !changes.minzoom.isFirstChange() || changes.maxzoom && !changes.maxzoom.isFirstChange()) {
        this.mapService.setLayerZoomRange(this.id(), this.minzoom(), this.maxzoom());
      }
    }
    ngOnDestroy() {
      if (this.layerAdded()) {
        const sourceIdAdded = this.sourceIdAdded();
        this.mapService.removeLayer(this.id());
        if (sourceIdAdded !== null) {
          // Clean up any automatically created source for this layer
          if (this.mapService.getSource(sourceIdAdded)) {
            this.mapService.removeSource(sourceIdAdded);
          }
        }
      }
    }
    init(bindEvents) {
      const layer = {
        layerOptions: {
          id: this.id(),
          type: this.type(),
          source: this.source(),
          metadata: this.metadata(),
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'source-layer': this.sourceLayer(),
          minzoom: this.minzoom(),
          maxzoom: this.maxzoom(),
          filter: this.filter(),
          layout: this.layout(),
          paint: this.paint()
        },
        layerEvents: {
          layerClick: this.layerClick,
          layerDblClick: this.layerDblClick,
          layerMouseDown: this.layerMouseDown,
          layerMouseUp: this.layerMouseUp,
          layerMouseEnter: this.layerMouseEnter,
          layerMouseLeave: this.layerMouseLeave,
          layerMouseMove: this.layerMouseMove,
          layerMouseOver: this.layerMouseOver,
          layerMouseOut: this.layerMouseOut,
          layerContextMenu: this.layerContextMenu,
          layerTouchStart: this.layerTouchStart,
          layerTouchEnd: this.layerTouchEnd,
          layerTouchCancel: this.layerTouchCancel
        }
      };
      if (this.removeSource() && typeof this.source() !== 'string') {
        // There is no id of an existing source bound to this layer
        if (this.mapService.getSource(this.id()) === undefined) {
          // A source with this layer id doesn't exist so it will be created automatically in the addLayer call below
          this.sourceIdAdded.set(this.id());
        }
      }
      this.mapService.addLayer(layer, bindEvents, this.before());
      const sourceIdAdded = this.sourceIdAdded();
      if (sourceIdAdded !== null) {
        const getSource = this.mapService.getSource(sourceIdAdded);
        if (getSource === undefined) {
          // If it wasn't created for some reason then we don't want to clean it up
          this.sourceIdAdded.set(null);
        }
      }
      this.layerAdded.set(true);
    }
    static {
      this.ɵfac = function LayerComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LayerComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: LayerComponent,
        selectors: [["mgl-layer"]],
        inputs: {
          id: [1, "id"],
          type: [1, "type"],
          source: [1, "source"],
          metadata: [1, "metadata"],
          sourceLayer: [1, "sourceLayer"],
          removeSource: [1, "removeSource"],
          filter: [1, "filter"],
          layout: [1, "layout"],
          paint: [1, "paint"],
          before: [1, "before"],
          minzoom: [1, "minzoom"],
          maxzoom: [1, "maxzoom"]
        },
        outputs: {
          layerClick: "layerClick",
          layerDblClick: "layerDblClick",
          layerMouseDown: "layerMouseDown",
          layerMouseUp: "layerMouseUp",
          layerMouseEnter: "layerMouseEnter",
          layerMouseLeave: "layerMouseLeave",
          layerMouseMove: "layerMouseMove",
          layerMouseOver: "layerMouseOver",
          layerMouseOut: "layerMouseOut",
          layerContextMenu: "layerContextMenu",
          layerTouchStart: "layerTouchStart",
          layerTouchEnd: "layerTouchEnd",
          layerTouchCancel: "layerTouchCancel"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function LayerComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return LayerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-map` - The main map component
 * @see [Map](https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/)
 *
 * @category Map Component
 *
 * @example
 * ```typescript
 * ...
 * @Component({
 *   template: `
 *   <mgl-map
 *     [style]="'https://demotiles.maplibre.org/style.json'"
 *     [zoom]="[9]"
 *     [center]="[-74.50, 40]"
 *     (mapLoad)="map = $event"
 *   ></mgl-map>
 *   `,
 * ...
 * })
 * export class DisplayMapComponent {
 *   map: Map; // MapLibre GL Map object (MapLibre is ran outside angular zone, keep that in mind when binding events from this object)
 * ...
 * }
 * ```
 */
let MapComponent = /*#__PURE__*/(() => {
  class MapComponent {
    get mapInstance() {
      return this.mapService.mapInstance;
    }
    constructor() {
      /** Init injection */
      this.mapService = inject(MapService);
      this.elementRef = inject(ElementRef);
      /** Init input */
      this.collectResourceTiming = input();
      /** Init input */
      this.crossSourceCollisions = input();
      /** Init input */
      this.customMapboxApiUrl = input();
      /** Init input */
      this.fadeDuration = input();
      /** Init input */
      this.hash = input();
      /** Init input */
      this.refreshExpiredTiles = input();
      /** Init input */
      this.failIfMajorPerformanceCaveat = input();
      /** Init input */
      this.bearingSnap = input();
      /** Init input */
      this.interactive = input();
      /** Init input */
      this.pitchWithRotate = input();
      /** Init input */
      this.clickTolerance = input();
      /** Init input */
      this.attributionControl = input();
      /** Init input */
      this.logoPosition = input();
      /** Init input */
      this.maxTileCacheSize = input();
      /** Init input */
      this.localIdeographFontFamily = input();
      /** Init input */
      this.preserveDrawingBuffer = input();
      /** Init input */
      this.trackResize = input();
      /** Init input */
      this.transformRequest = input();
      /** Init input */
      this.bounds = input();
      /** Init input */
      this.antialias = input();
      /** Init input */
      this.locale = input();
      /** Init input */
      this.cooperativeGestures = input();
      /** Dynamic input */
      this.minZoom = input();
      /** Dynamic input */
      this.maxZoom = input();
      /** Dynamic input */
      this.minPitch = input();
      /** Dynamic input */
      this.maxPitch = input();
      /** Dynamic input */
      this.scrollZoom = input();
      /** Dynamic input */
      this.dragRotate = input();
      /** Dynamic input */
      this.touchPitch = input();
      /** Dynamic input */
      this.touchZoomRotate = input();
      /** Dynamic input */
      this.doubleClickZoom = input();
      /** Dynamic input */
      this.keyboard = input();
      /** Dynamic input */
      this.dragPan = input();
      /** Dynamic input */
      this.boxZoom = input();
      /** Dynamic input */
      this.style = input.required();
      /** Dynamic input */
      this.center = input();
      /** Dynamic input */
      this.maxBounds = input();
      /** Dynamic input */
      this.zoom = input();
      /** Dynamic input */
      this.bearing = input();
      /** Dynamic input */
      this.pitch = input();
      /** Dynamic input */
      this.fitBoundsOptions = input(); // First value goes to options.fitBoundsOptions. Subsequents changes are passed to fitBounds
      /** Dynamic input */
      this.renderWorldCopies = input();
      /** Dynamic input */
      this.terrain = input();
      /** Added by ngx-mapbox-gl */
      this.movingMethod = input('flyTo');
      this.movingOptions = input();
      // => First value is a alias to bounds input (since mapbox 0.53.0). Subsequents changes are passed to fitBounds
      this.fitBounds = input();
      this.fitScreenCoordinates = input();
      this.centerWithPanTo = input();
      this.panToOptions = input();
      this.cursorStyle = input();
      this.mapResize = output();
      this.mapRemove = output();
      this.mapMouseDown = output();
      this.mapMouseUp = output();
      this.mapMouseMove = output();
      this.mapClick = output();
      this.mapDblClick = output();
      this.mapMouseOver = output();
      this.mapMouseOut = output();
      this.mapContextMenu = output();
      this.mapTouchStart = output();
      this.mapTouchEnd = output();
      this.mapTouchMove = output();
      this.mapTouchCancel = output();
      this.mapWheel = output();
      this.moveStart = output();
      this.move = output();
      this.moveEnd = output();
      this.mapDragStart = output();
      this.mapDrag = output();
      this.mapDragEnd = output();
      this.zoomStart = output();
      this.zoomEvt = output();
      this.zoomEnd = output();
      this.rotateStart = output();
      this.rotate = output();
      this.rotateEnd = output();
      this.pitchStart = output();
      this.pitchEvt = output();
      this.pitchEnd = output();
      this.boxZoomStart = output();
      this.boxZoomEnd = output();
      this.boxZoomCancel = output();
      this.webGlContextLost = output();
      this.webGlContextRestored = output();
      this.mapLoad = output();
      this.idle = output();
      this.render = output();
      this.mapError = output();
      this.data = output();
      this.styleData = output();
      this.sourceData = output();
      this.dataLoading = output();
      this.styleDataLoading = output();
      this.sourceDataLoading = output();
      this.styleImageMissing = output();
      this.mapContainer = viewChild.required('container');
      afterNextRender(() => {
        if (this.preserveDrawingBuffer()) {
          // This is to allow better interaction with the map state
          const htmlElement = this.elementRef.nativeElement;
          htmlElement.setAttribute('data-cy', 'map');
          this.mapLoad.subscribe(() => {
            htmlElement.setAttribute('data-loaded', 'true');
          });
          this.idle.subscribe(() => {
            htmlElement.setAttribute('data-idle', 'true');
          });
          this.render.subscribe(() => {
            htmlElement.removeAttribute('data-idle');
          });
        }
        this.mapService.setup({
          mapOptions: {
            collectResourceTiming: this.collectResourceTiming(),
            container: this.mapContainer().nativeElement,
            crossSourceCollisions: this.crossSourceCollisions(),
            fadeDuration: this.fadeDuration(),
            minZoom: this.minZoom(),
            maxZoom: this.maxZoom(),
            minPitch: this.minPitch(),
            maxPitch: this.maxPitch(),
            style: this.style(),
            hash: this.hash(),
            interactive: this.interactive(),
            bearingSnap: this.bearingSnap(),
            pitchWithRotate: this.pitchWithRotate(),
            clickTolerance: this.clickTolerance(),
            attributionControl: this.attributionControl(),
            logoPosition: this.logoPosition(),
            failIfMajorPerformanceCaveat: this.failIfMajorPerformanceCaveat(),
            preserveDrawingBuffer: this.preserveDrawingBuffer(),
            refreshExpiredTiles: this.refreshExpiredTiles(),
            maxBounds: this.maxBounds(),
            scrollZoom: this.scrollZoom(),
            boxZoom: this.boxZoom(),
            dragRotate: this.dragRotate(),
            dragPan: this.dragPan(),
            keyboard: this.keyboard(),
            doubleClickZoom: this.doubleClickZoom(),
            touchPitch: this.touchPitch(),
            touchZoomRotate: this.touchZoomRotate(),
            trackResize: this.trackResize(),
            center: this.center(),
            zoom: this.zoom(),
            bearing: this.bearing(),
            pitch: this.pitch(),
            renderWorldCopies: this.renderWorldCopies(),
            maxTileCacheSize: this.maxTileCacheSize(),
            localIdeographFontFamily: this.localIdeographFontFamily(),
            transformRequest: this.transformRequest(),
            bounds: this.bounds() ? this.bounds() : this.fitBounds(),
            fitBoundsOptions: this.fitBoundsOptions(),
            antialias: this.antialias(),
            locale: this.locale,
            cooperativeGestures: this.cooperativeGestures(),
            terrain: this.terrain()
          },
          mapEvents: this
        });
        const cursorStyle = this.cursorStyle();
        if (cursorStyle) {
          this.mapService.changeCanvasCursor(cursorStyle);
        }
      });
      afterRender(() => {
        this.mapService.clearMapElements();
      });
    }
    ngOnDestroy() {
      this.mapService.destroyMap();
    }
    async ngOnChanges(changes) {
      await firstValueFrom(this.mapService.mapCreated$);
      const zoom = this.zoom();
      const bearing = this.bearing();
      const pitch = this.pitch();
      const center = this.center();
      if (changes.cursorStyle && !changes.cursorStyle.isFirstChange()) {
        this.mapService.changeCanvasCursor(changes.cursorStyle.currentValue);
      }
      if (changes.minZoom && !changes.minZoom.isFirstChange()) {
        this.mapService.updateMinZoom(changes.minZoom.currentValue);
      }
      if (changes.maxZoom && !changes.maxZoom.isFirstChange()) {
        this.mapService.updateMaxZoom(changes.maxZoom.currentValue);
      }
      if (changes.minPitch && !changes.minPitch.isFirstChange()) {
        this.mapService.updateMinPitch(changes.minPitch.currentValue);
      }
      if (changes.maxPitch && !changes.maxPitch.isFirstChange()) {
        this.mapService.updateMaxPitch(changes.maxPitch.currentValue);
      }
      if (changes.renderWorldCopies && !changes.renderWorldCopies.isFirstChange()) {
        this.mapService.updateRenderWorldCopies(changes.renderWorldCopies.currentValue);
      }
      if (changes.scrollZoom && !changes.scrollZoom.isFirstChange()) {
        this.mapService.updateScrollZoom(changes.scrollZoom.currentValue);
      }
      if (changes.dragRotate && !changes.dragRotate.isFirstChange()) {
        this.mapService.updateDragRotate(changes.dragRotate.currentValue);
      }
      if (changes.touchPitch && !changes.touchPitch.isFirstChange()) {
        this.mapService.updateTouchPitch(changes.touchPitch.currentValue);
      }
      if (changes.touchZoomRotate && !changes.touchZoomRotate.isFirstChange()) {
        this.mapService.updateTouchZoomRotate(changes.touchZoomRotate.currentValue);
      }
      if (changes.doubleClickZoom && !changes.doubleClickZoom.isFirstChange()) {
        this.mapService.updateDoubleClickZoom(changes.doubleClickZoom.currentValue);
      }
      if (changes.keyboard && !changes.keyboard.isFirstChange()) {
        this.mapService.updateKeyboard(changes.keyboard.currentValue);
      }
      if (changes.dragPan && !changes.dragPan.isFirstChange()) {
        this.mapService.updateDragPan(changes.dragPan.currentValue);
      }
      if (changes.boxZoom && !changes.boxZoom.isFirstChange()) {
        this.mapService.updateBoxZoom(changes.boxZoom.currentValue);
      }
      if (changes.style && !changes.style.isFirstChange()) {
        this.mapService.updateStyle(changes.style.currentValue);
      }
      if (changes.maxBounds && !changes.maxBounds.isFirstChange()) {
        this.mapService.updateMaxBounds(changes.maxBounds.currentValue);
      }
      if (changes.fitBounds && changes.fitBounds.currentValue && !changes.fitBounds.isFirstChange()) {
        this.mapService.fitBounds(changes.fitBounds.currentValue, this.fitBoundsOptions());
      }
      if (changes.fitScreenCoordinates && changes.fitScreenCoordinates.currentValue) {
        if ((center || zoom || pitch || this.fitBounds()) && changes.fitScreenCoordinates.isFirstChange()) {
          console.warn('[ngx-maplibre-gl] center / zoom / pitch / fitBounds inputs are being overridden by fitScreenCoordinates input');
        }
        this.mapService.fitScreenCoordinates(changes.fitScreenCoordinates.currentValue, bearing ? bearing[0] : 0, this.movingOptions());
      }
      if (this.centerWithPanTo() && changes.center && !changes.center.isFirstChange() && !changes.zoom && !changes.bearing && !changes.pitch) {
        this.mapService.panTo(this.center(), this.panToOptions());
      } else if (changes.center && !changes.center.isFirstChange() || changes.zoom && !changes.zoom.isFirstChange() || changes.bearing && !changes.bearing.isFirstChange() && !changes.fitScreenCoordinates || changes.pitch && !changes.pitch.isFirstChange()) {
        this.mapService.move(this.movingMethod(), this.movingOptions(), changes.zoom && zoom ? zoom[0] : undefined, changes.center ? center : undefined, changes.bearing && bearing ? bearing[0] : undefined, changes.pitch && pitch ? pitch[0] : undefined);
      }
      if (changes.terrain && !changes.terrain.isFirstChange()) {
        this.mapService.updateTerrain(changes.terrain.currentValue);
      }
    }
    static {
      this.ɵfac = function MapComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MapComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MapComponent,
        selectors: [["mgl-map"]],
        viewQuery: function MapComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuerySignal(ctx.mapContainer, _c2, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        inputs: {
          collectResourceTiming: [1, "collectResourceTiming"],
          crossSourceCollisions: [1, "crossSourceCollisions"],
          customMapboxApiUrl: [1, "customMapboxApiUrl"],
          fadeDuration: [1, "fadeDuration"],
          hash: [1, "hash"],
          refreshExpiredTiles: [1, "refreshExpiredTiles"],
          failIfMajorPerformanceCaveat: [1, "failIfMajorPerformanceCaveat"],
          bearingSnap: [1, "bearingSnap"],
          interactive: [1, "interactive"],
          pitchWithRotate: [1, "pitchWithRotate"],
          clickTolerance: [1, "clickTolerance"],
          attributionControl: [1, "attributionControl"],
          logoPosition: [1, "logoPosition"],
          maxTileCacheSize: [1, "maxTileCacheSize"],
          localIdeographFontFamily: [1, "localIdeographFontFamily"],
          preserveDrawingBuffer: [1, "preserveDrawingBuffer"],
          trackResize: [1, "trackResize"],
          transformRequest: [1, "transformRequest"],
          bounds: [1, "bounds"],
          antialias: [1, "antialias"],
          locale: [1, "locale"],
          cooperativeGestures: [1, "cooperativeGestures"],
          minZoom: [1, "minZoom"],
          maxZoom: [1, "maxZoom"],
          minPitch: [1, "minPitch"],
          maxPitch: [1, "maxPitch"],
          scrollZoom: [1, "scrollZoom"],
          dragRotate: [1, "dragRotate"],
          touchPitch: [1, "touchPitch"],
          touchZoomRotate: [1, "touchZoomRotate"],
          doubleClickZoom: [1, "doubleClickZoom"],
          keyboard: [1, "keyboard"],
          dragPan: [1, "dragPan"],
          boxZoom: [1, "boxZoom"],
          style: [1, "style"],
          center: [1, "center"],
          maxBounds: [1, "maxBounds"],
          zoom: [1, "zoom"],
          bearing: [1, "bearing"],
          pitch: [1, "pitch"],
          fitBoundsOptions: [1, "fitBoundsOptions"],
          renderWorldCopies: [1, "renderWorldCopies"],
          terrain: [1, "terrain"],
          movingMethod: [1, "movingMethod"],
          movingOptions: [1, "movingOptions"],
          fitBounds: [1, "fitBounds"],
          fitScreenCoordinates: [1, "fitScreenCoordinates"],
          centerWithPanTo: [1, "centerWithPanTo"],
          panToOptions: [1, "panToOptions"],
          cursorStyle: [1, "cursorStyle"]
        },
        outputs: {
          mapResize: "mapResize",
          mapRemove: "mapRemove",
          mapMouseDown: "mapMouseDown",
          mapMouseUp: "mapMouseUp",
          mapMouseMove: "mapMouseMove",
          mapClick: "mapClick",
          mapDblClick: "mapDblClick",
          mapMouseOver: "mapMouseOver",
          mapMouseOut: "mapMouseOut",
          mapContextMenu: "mapContextMenu",
          mapTouchStart: "mapTouchStart",
          mapTouchEnd: "mapTouchEnd",
          mapTouchMove: "mapTouchMove",
          mapTouchCancel: "mapTouchCancel",
          mapWheel: "mapWheel",
          moveStart: "moveStart",
          move: "move",
          moveEnd: "moveEnd",
          mapDragStart: "mapDragStart",
          mapDrag: "mapDrag",
          mapDragEnd: "mapDragEnd",
          zoomStart: "zoomStart",
          zoomEvt: "zoomEvt",
          zoomEnd: "zoomEnd",
          rotateStart: "rotateStart",
          rotate: "rotate",
          rotateEnd: "rotateEnd",
          pitchStart: "pitchStart",
          pitchEvt: "pitchEvt",
          pitchEnd: "pitchEnd",
          boxZoomStart: "boxZoomStart",
          boxZoomEnd: "boxZoomEnd",
          boxZoomCancel: "boxZoomCancel",
          webGlContextLost: "webGlContextLost",
          webGlContextRestored: "webGlContextRestored",
          mapLoad: "mapLoad",
          idle: "idle",
          render: "render",
          mapError: "mapError",
          data: "data",
          styleData: "styleData",
          sourceData: "sourceData",
          dataLoading: "dataLoading",
          styleDataLoading: "styleDataLoading",
          sourceDataLoading: "sourceDataLoading",
          styleImageMissing: "styleImageMissing"
        },
        features: [i0.ɵɵProvidersFeature([MapService]), i0.ɵɵNgOnChangesFeature],
        decls: 2,
        vars: 0,
        consts: [["container", ""]],
        template: function MapComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "div", null, 0);
          }
        },
        styles: ["[_nghost-%COMP%]{display:block}div[_ngcontent-%COMP%]{height:100%;width:100%}"],
        changeDetection: 0
      });
    }
  }
  return MapComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-marker` - a marker component
 * @see [Marker](https://maplibre.org/maplibre-gl-js/docs/API/classes/Marker/)
 *
 * @category Components
 *
 * @example
 * ```html
 * ...
 * <mgl-map ...>
 *   <mgl-marker [lngLat]="[-66.324462890625, -16.024695711685304]">
 *     <div (click)="alert('Foo')" class="marker">Hello</div>
 *   </mgl-marker>
 * </mgl-map>
 * ```
 *
 * Note: Only use this if you **really** need to use HTML/Angular component to render your symbol. These markers are slow compared to a `Layer` of symbol because they're not rendered using WebGL.
 */
let MarkerComponent = /*#__PURE__*/(() => {
  class MarkerComponent {
    constructor() {
      /** Init injection */
      this.mapService = inject(MapService);
      this.destroyRef = inject(DestroyRef);
      /** Init inputs */
      this.offset = input();
      this.anchor = input();
      this.clickTolerance = input();
      this.color = input();
      this.scale = input();
      this.opacity = input();
      this.opacityWhenCovered = input();
      this.subpixelPositioning = input();
      /** Dynamic input */
      this.feature = input();
      this.lngLat = input();
      this.draggable = input();
      this.popupShown = input();
      this.className = input();
      this.pitchAlignment = input();
      this.rotationAlignment = input();
      this.rotation = input();
      this.markerDragStart = output();
      this.markerDragEnd = output();
      this.markerDrag = output();
      this.content = viewChild.required('content');
      this.markerInstance = signal(null);
      afterNextRender(() => {
        this.mapService.mapCreated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          const marker = this.mapService.addMarker({
            markersOptions: {
              element: this.content().nativeElement,
              feature: this.feature(),
              lngLat: this.lngLat(),
              offset: this.offset(),
              anchor: this.anchor(),
              color: this.color(),
              scale: this.scale(),
              draggable: !!this.draggable(),
              clickTolerance: this.clickTolerance(),
              rotation: this.rotation(),
              rotationAlignment: this.rotationAlignment(),
              pitchAlignment: this.pitchAlignment(),
              opacity: this.opacity(),
              opacityWhenCovered: this.opacityWhenCovered(),
              subpixelPositioning: this.subpixelPositioning()
            },
            markersEvents: {
              markerDragStart: this.markerDragStart,
              markerDrag: this.markerDrag,
              markerDragEnd: this.markerDragEnd
            }
          });
          this.markerInstance.set(marker);
        });
      });
      this.destroyRef.onDestroy(() => this.removeMarker());
    }
    ngOnInit() {
      if (this.feature() && this.lngLat()) {
        throw new Error('feature and lngLat input are mutually exclusive');
      }
    }
    ngOnChanges(changes) {
      const markerInstance = this.markerInstance();
      if (changes.lngLat && !changes.lngLat.isFirstChange()) {
        markerInstance.setLngLat(changes.lngLat.currentValue);
      }
      if (changes.feature && !changes.feature.isFirstChange()) {
        markerInstance.setLngLat(changes.feature.currentValue.geometry.coordinates);
      }
      if (changes.draggable && !changes.draggable.isFirstChange()) {
        markerInstance.setDraggable(!!changes.draggable.currentValue);
      }
      if (changes.popupShown && !changes.popupShown.isFirstChange()) {
        changes.popupShown.currentValue ? markerInstance.getPopup().addTo(this.mapService.mapInstance) : markerInstance.getPopup().remove();
      }
      if (changes.pitchAlignment && !changes.pitchAlignment.isFirstChange()) {
        markerInstance.setPitchAlignment(changes.pitchAlignment.currentValue);
      }
      if (changes.rotationAlignment && !changes.rotationAlignment.isFirstChange()) {
        markerInstance.setRotationAlignment(changes.rotationAlignment.currentValue);
      }
      if (changes.rotation && !changes.rotation.isFirstChange()) {
        markerInstance.setRotation(changes.rotation.currentValue);
      }
    }
    removeMarker() {
      this.mapService.removeMarker(this.markerInstance());
      this.markerInstance.set(null);
    }
    togglePopup() {
      this.markerInstance().togglePopup();
    }
    static {
      this.ɵfac = function MarkerComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MarkerComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MarkerComponent,
        selectors: [["mgl-marker"]],
        viewQuery: function MarkerComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuerySignal(ctx.content, _c0, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        inputs: {
          offset: [1, "offset"],
          anchor: [1, "anchor"],
          clickTolerance: [1, "clickTolerance"],
          color: [1, "color"],
          scale: [1, "scale"],
          opacity: [1, "opacity"],
          opacityWhenCovered: [1, "opacityWhenCovered"],
          subpixelPositioning: [1, "subpixelPositioning"],
          feature: [1, "feature"],
          lngLat: [1, "lngLat"],
          draggable: [1, "draggable"],
          popupShown: [1, "popupShown"],
          className: [1, "className"],
          pitchAlignment: [1, "pitchAlignment"],
          rotationAlignment: [1, "rotationAlignment"],
          rotation: [1, "rotation"]
        },
        outputs: {
          markerDragStart: "markerDragStart",
          markerDragEnd: "markerDragEnd",
          markerDrag: "markerDrag"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        ngContentSelectors: _c1,
        decls: 3,
        vars: 2,
        consts: [["content", ""]],
        template: function MarkerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", null, 0);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵclassMap(ctx.className());
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return MarkerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * a template directive for point for {@link MarkersForClustersComponent}
 *
 * @category Directives
 */
let PointDirective = /*#__PURE__*/(() => {
  class PointDirective {
    static {
      this.ɵfac = function PointDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PointDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: PointDirective,
        selectors: [["ng-template", "mglPoint", ""]]
      });
    }
  }
  return PointDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * a template directive for clustered point for {@link MarkersForClustersComponent}
 *
 * @category Directives
 */
let ClusterPointDirective = /*#__PURE__*/(() => {
  class ClusterPointDirective {
    static {
      this.ɵfac = function ClusterPointDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ClusterPointDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ClusterPointDirective,
        selectors: [["ng-template", "mglClusterPoint", ""]]
      });
    }
  }
  return ClusterPointDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let uniqId = 0;
/**
 * [ngx] `mgl-markers-for-clusters` - an HTML marker component for clustered points.
 * Requires a geojson source that is clustered.
 *
 * @category Components
 *
 * @example
 * ```html
 * ...
 * <mgl-map ...>
 *   <mgl-markers-for-cluster [source]="myGeoJsonclusteredSource">
 *     <ng-template mglPoint let-feature> Marker! </ng-template>
 *     <ng-template mglClusterPoint let-feature>
 *       ClusterId: {{feature.properties?.cluster_id}}, Points:
 *       {{feature.properties?.point_count}}
 *     </ng-template>
 *   </mgl-markers-for-cluster>
 * </mgl-map>
 * ```
 *
 * Note: Only use this if you **really** need to use HTML/Angular component to render your symbols. This is **slower** than rendering symbols in WebGL.
 */
let MarkersForClustersComponent = /*#__PURE__*/(() => {
  class MarkersForClustersComponent {
    constructor() {
      this.destroyRef = inject(DestroyRef);
      this.mapService = inject(MapService);
      this.ngZone = inject(NgZone);
      /** Init input */
      this.source = input.required();
      /** @hidden */
      this.pointTpl = contentChild(PointDirective, {
        read: TemplateRef
      });
      /** @hidden */
      this.clusterPointTpl = contentChild(ClusterPointDirective, {
        read: TemplateRef
      });
      /** @hidden */
      this.clusterPoints = signal([]);
      /** @hidden */
      this.layerId = `mgl-markers-for-clusters-${uniqId++}`;
      afterNextRender(() => {
        const clusterDataUpdate = () => fromEvent(this.mapService.mapInstance, 'data').pipe(filter(e => e.sourceId === this.source() && e.sourceDataType !== 'metadata' && this.mapService.mapInstance.isSourceLoaded(this.source())));
        this.mapService.mapCreated$.pipe(switchMap(clusterDataUpdate), switchMap(() => merge(fromEvent(this.mapService.mapInstance, 'move'), fromEvent(this.mapService.mapInstance, 'moveend')).pipe(startWith(undefined)))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.ngZone.run(() => {
            this.updateCluster();
          });
        });
      });
    }
    updateCluster() {
      const params = this.getClusterParams(this.pointTpl());
      this.clusterPoints.set(this.mapService.mapInstance.queryRenderedFeatures(params));
    }
    getClusterParams(pointTpl) {
      if (!pointTpl) {
        return {
          layers: [this.layerId],
          filter: ['==', 'cluster', true]
        };
      }
      return {
        layers: [this.layerId]
      };
    }
    static {
      this.ɵfac = function MarkersForClustersComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MarkersForClustersComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MarkersForClustersComponent,
        selectors: [["mgl-markers-for-clusters"]],
        contentQueries: function MarkersForClustersComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuerySignal(dirIndex, ctx.pointTpl, PointDirective, 5, TemplateRef);
            i0.ɵɵcontentQuerySignal(dirIndex, ctx.clusterPointTpl, ClusterPointDirective, 5, TemplateRef);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance(2);
          }
        },
        inputs: {
          source: [1, "source"]
        },
        decls: 3,
        vars: 4,
        consts: [["type", "circle", 3, "id", "source", "paint"], [3, "feature"], [4, "ngTemplateOutlet", "ngTemplateOutletContext"]],
        template: function MarkersForClustersComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "mgl-layer", 0);
            i0.ɵɵrepeaterCreate(1, MarkersForClustersComponent_For_2_Template, 2, 1, null, null, i0.ɵɵrepeaterTrackByIndex);
          }
          if (rf & 2) {
            i0.ɵɵproperty("id", ctx.layerId)("source", ctx.source())("paint", i0.ɵɵpureFunction0(3, _c3));
            i0.ɵɵadvance();
            i0.ɵɵrepeater(ctx.clusterPoints());
          }
        },
        dependencies: [LayerComponent, MarkerComponent, NgTemplateOutlet],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return MarkersForClustersComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-popup` - a popup component
 * @see [Popup](https://maplibre.org/maplibre-gl-js/docs/API/classes/Popup/)
 *
 * @category Components
 *
 * @example
 * ```html
 * ...
 * <mgl-map ...>
 *   <mgl-popup [lngLat]="[-96, 37.8]" [closeOnClick]="false">
 *     <h1>Hello world !</h1>
 *   </mgl-popup>
 *   ...
 *   <mgl-marker #myMarker ...> ... </mgl-marker>
 *   <mgl-popup [marker]="myMarker"> Hello from marker ! </mgl-popup>
 * </mgl-map>
 * ```
 */
let PopupComponent = /*#__PURE__*/(() => {
  class PopupComponent {
    constructor() {
      /** Init injection */
      this.destroyRef = inject(DestroyRef);
      this.mapService = inject(MapService);
      /** Init input */
      this.closeButton = input();
      /** Init input */
      this.closeOnClick = input();
      /** Init input */
      this.closeOnMove = input();
      /** Init input */
      this.focusAfterOpen = input();
      /** Init input */
      this.anchor = input();
      /** Init input */
      this.className = input();
      /** Init input */
      this.maxWidth = input();
      /** Init input */
      this.subpixelPositioning = input();
      /**
       * Dynamic input [ngx]
       * Mutually exclusive with `lngLat`
       */
      this.feature = input();
      /** Dynamic input */
      this.lngLat = input();
      /**
       * Dynamic input [ngx]
       * The targeted marker
       */
      this.marker = input();
      /** Dynamic input */
      this.offset = input();
      this.popupClose = output();
      this.popupOpen = output();
      /** @hidden */
      this.content = viewChild.required('content');
      this.popupInstance = null;
      afterNextRender(() => {
        this.popupInstance = this.createPopup();
        this.addPopup(this.popupInstance).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      });
      this.destroyRef.onDestroy(() => this.removePopupFromMarker());
    }
    ngOnInit() {
      if (this.lngLat() && this.marker() || this.feature() && this.lngLat() || this.feature() && this.marker()) {
        throw new Error('marker, lngLat, feature input are mutually exclusive');
      }
    }
    ngOnChanges(changes) {
      if (changes.feature && !changes.feature.isFirstChange()) {
        const newlngLat = this.getLngLat(this.lngLat(), this.feature());
        this.mapService.removePopupFromMap(this.popupInstance, true);
        const popupInstanceTmp = this.createPopup();
        this.mapService.addPopupToMap(popupInstanceTmp, newlngLat, this.popupInstance.isOpen());
        this.popupInstance = popupInstanceTmp;
      }
      if (changes.lngLat && !changes.lngLat.isFirstChange()) {
        this.popupInstance.setLngLat(changes.lngLat.currentValue);
      }
      if (changes.marker && !changes.marker.isFirstChange()) {
        const previousMarker = changes.marker.previousValue;
        const previousMarkerInstance = previousMarker.markerInstance();
        if (previousMarkerInstance) {
          this.mapService.removePopupFromMarker(previousMarkerInstance);
        }
        const markerInstance = this.marker()?.markerInstance();
        if (markerInstance && this.popupInstance) {
          this.mapService.addPopupToMarker(markerInstance, this.popupInstance);
        }
      }
      if (changes.offset && !changes.offset.isFirstChange() && this.popupInstance) {
        this.popupInstance.setOffset(changes.offset.currentValue);
      }
    }
    createPopup() {
      return this.mapService.createPopup({
        popupOptions: {
          closeButton: this.closeButton(),
          closeOnClick: this.closeOnClick(),
          closeOnMove: this.closeOnMove(),
          focusAfterOpen: this.focusAfterOpen(),
          anchor: this.anchor(),
          offset: this.offset(),
          className: this.className(),
          maxWidth: this.maxWidth(),
          subpixelPositioning: this.subpixelPositioning()
        },
        popupEvents: {
          popupOpen: this.popupOpen,
          popupClose: this.popupClose
        }
      }, this.content().nativeElement);
    }
    addPopup(popup) {
      return this.mapService.mapCreated$.pipe(tap$1(() => {
        const lngLat = this.lngLat();
        const feature = this.feature();
        const markerInstance = this.marker()?.markerInstance();
        if (lngLat || feature) {
          this.mapService.addPopupToMap(popup, this.getLngLat(lngLat, feature));
        } else if (markerInstance) {
          this.mapService.addPopupToMarker(markerInstance, popup);
        } else {
          throw new Error('mgl-popup need either lngLat/marker/feature to be set');
        }
      }));
    }
    removePopupFromMarker() {
      if (this.popupInstance) {
        const markerInstance = this.marker()?.markerInstance();
        if (this.lngLat() || this.feature()) {
          this.mapService.removePopupFromMap(this.popupInstance, true);
        } else if (markerInstance) {
          this.mapService.removePopupFromMarker(markerInstance);
        }
      }
      this.popupInstance = null;
    }
    getLngLat(lngLat, feature) {
      if (lngLat) {
        return lngLat;
      } else if (feature) {
        return feature.geometry.coordinates;
      }
      throw new Error('lngLat or feature value is required');
    }
    static {
      this.ɵfac = function PopupComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PopupComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: PopupComponent,
        selectors: [["mgl-popup"]],
        viewQuery: function PopupComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuerySignal(ctx.content, _c0, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        inputs: {
          closeButton: [1, "closeButton"],
          closeOnClick: [1, "closeOnClick"],
          closeOnMove: [1, "closeOnMove"],
          focusAfterOpen: [1, "focusAfterOpen"],
          anchor: [1, "anchor"],
          className: [1, "className"],
          maxWidth: [1, "maxWidth"],
          subpixelPositioning: [1, "subpixelPositioning"],
          feature: [1, "feature"],
          lngLat: [1, "lngLat"],
          marker: [1, "marker"],
          offset: [1, "offset"]
        },
        outputs: {
          popupClose: "popupClose",
          popupOpen: "popupOpen"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        ngContentSelectors: _c1,
        decls: 3,
        vars: 0,
        consts: [["content", ""], ["data-cy", "mgl-popup"]],
        template: function PopupComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 1, 0);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return PopupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-canvas-source` - a canvas source component
 * @see [canvas](https://maplibre.org/maplibre-style-spec/sources/#canvas)
 *
 * @category Source Components
 */
let CanvasSourceComponent = /*#__PURE__*/(() => {
  class CanvasSourceComponent {
    constructor() {
      /** Init injections */
      this.sourceDirective = inject(SourceDirective);
      /** Dynamic input */
      this.coordinates = input.required();
      /** Dynamic input */
      this.canvas = input.required();
      /** Dynamic input */
      this.animate = input();
      this.sourceDirective.loadSource$.pipe(tap$1(() => this.sourceDirective.addSource(this.getCanvasSourceSpecification())), takeUntilDestroyed()).subscribe();
    }
    ngOnChanges(changes) {
      if (!this.sourceDirective.sourceId()) {
        return;
      }
      if (changes.canvas && !changes.canvas.isFirstChange() || changes.animate && !changes.animate.isFirstChange()) {
        this.sourceDirective.refresh();
      } else if (changes.coordinates && !changes.coordinates.isFirstChange()) {
        const source = this.sourceDirective.getSource();
        if (source === undefined) {
          return;
        }
        source.setCoordinates(changes.coordinates.currentValue);
      }
    }
    getCanvasSourceSpecification() {
      return {
        type: 'canvas',
        coordinates: this.coordinates(),
        canvas: this.canvas(),
        animate: this.animate()
      };
    }
    static {
      this.ɵfac = function CanvasSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CanvasSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: CanvasSourceComponent,
        selectors: [["mgl-canvas-source"]],
        inputs: {
          coordinates: [1, "coordinates"],
          canvas: [1, "canvas"],
          animate: [1, "animate"]
        },
        features: [i0.ɵɵHostDirectivesFeature([{
          directive: SourceDirective,
          inputs: ["id", "id"]
        }]), i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function CanvasSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return CanvasSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-image-source` - an image source component
 * @see [image](https://maplibre.org/maplibre-style-spec/sources/#image)
 *
 * @category Source Components
 */
let ImageSourceComponent = /*#__PURE__*/(() => {
  class ImageSourceComponent {
    constructor() {
      /** Init injection */
      this.destroyRef = inject(DestroyRef);
      this.mapService = inject(MapService);
      /** Init inputs */
      this.id = input.required();
      /** Dynamic inputs */
      this.url = input.required();
      this.coordinates = input.required();
      this.type = 'image';
      this.sourceId = signal(null);
    }
    ngOnInit() {
      this.mapService.mapLoaded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.addSource());
    }
    ngOnChanges(changes) {
      const sourceId = this.sourceId();
      if (sourceId === null) {
        return;
      }
      const source = this.mapService.getSource(sourceId);
      if (source === undefined) {
        return;
      }
      source.updateImage({
        url: changes.url === undefined ? undefined : this.url(),
        coordinates: changes.coordinates === undefined ? undefined : this.coordinates()
      });
    }
    ngOnDestroy() {
      const sourceId = this.sourceId();
      if (sourceId !== null) {
        this.mapService.removeSource(sourceId);
        this.sourceId.set(null);
      }
    }
    addSource() {
      const imageSource = {
        type: 'image',
        url: this.url(),
        coordinates: this.coordinates()
      };
      this.mapService.addSource(this.id(), imageSource);
      this.sourceId.set(this.id());
    }
    static {
      this.ɵfac = function ImageSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ImageSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: ImageSourceComponent,
        selectors: [["mgl-image-source"]],
        inputs: {
          id: [1, "id"],
          url: [1, "url"],
          coordinates: [1, "coordinates"]
        },
        features: [i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function ImageSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return ImageSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-raster-dem-source` - a raster DEM source
 * @see [raster DEM](https://maplibre.org/maplibre-style-spec/sources/#raster-dem)
 *
 * @category Source Components
 */
let RasterDemSourceComponent = /*#__PURE__*/(() => {
  class RasterDemSourceComponent {
    constructor() {
      /** Init injections */
      this.sourceDirective = inject(SourceDirective);
      /** Dynamic input */
      this.url = input();
      /** Dynamic input */
      this.tiles = input();
      /** Dynamic input */
      this.bounds = input();
      /** Dynamic input */
      this.minzoom = input();
      /** Dynamic input */
      this.maxzoom = input();
      /** Dynamic input */
      this.tileSize = input();
      /** Dynamic input */
      this.attribution = input();
      /** Dynamic input */
      this.encoding = input();
      this.sourceDirective.loadSource$.pipe(tap(() => this.sourceDirective.addSource(this.getRasterDEMSourceSpecification())), takeUntilDestroyed()).subscribe();
    }
    ngOnChanges(changes) {
      if (!this.sourceDirective.sourceId()) {
        return;
      }
      if (changes.url && !changes.url.isFirstChange() || changes.tiles && !changes.tiles.isFirstChange() || changes.bounds && !changes.bounds.isFirstChange() || changes.minzoom && !changes.minzoom.isFirstChange() || changes.maxzoom && !changes.maxzoom.isFirstChange() || changes.tileSize && !changes.tileSize.isFirstChange() || changes.attribution && !changes.attribution.isFirstChange() || changes.encoding && !changes.encoding.isFirstChange()) {
        this.sourceDirective.refresh();
      }
    }
    getRasterDEMSourceSpecification() {
      return {
        type: 'raster-dem',
        url: this.url(),
        tiles: this.tiles(),
        bounds: this.bounds(),
        minzoom: this.minzoom(),
        maxzoom: this.maxzoom(),
        tileSize: this.tileSize(),
        attribution: this.attribution(),
        encoding: this.encoding()
      };
    }
    static {
      this.ɵfac = function RasterDemSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RasterDemSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: RasterDemSourceComponent,
        selectors: [["mgl-raster-dem-source"]],
        inputs: {
          url: [1, "url"],
          tiles: [1, "tiles"],
          bounds: [1, "bounds"],
          minzoom: [1, "minzoom"],
          maxzoom: [1, "maxzoom"],
          tileSize: [1, "tileSize"],
          attribution: [1, "attribution"],
          encoding: [1, "encoding"]
        },
        features: [i0.ɵɵHostDirectivesFeature([{
          directive: SourceDirective,
          inputs: ["id", "id"]
        }]), i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function RasterDemSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return RasterDemSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-raster-source` - a raster source component
 * @see [raster](https://maplibre.org/maplibre-style-spec/sources/#raster)
 *
 * @category Source Components
 */
let RasterSourceComponent = /*#__PURE__*/(() => {
  class RasterSourceComponent {
    constructor() {
      /** Init injections */
      this.sourceDirective = inject(SourceDirective);
      /** Dynamic input */
      this.url = input();
      /** Dynamic input */
      this.tiles = input();
      /** Dynamic input */
      this.bounds = input();
      /** Dynamic input */
      this.scheme = input();
      /** Dynamic input */
      this.minzoom = input();
      /** Dynamic input */
      this.maxzoom = input();
      /** Dynamic input */
      this.tileSize = input();
      /** Dynamic input */
      this.attribution = input();
      this.sourceDirective.loadSource$.pipe(tap$1(() => this.sourceDirective.addSource(this.getRasterSourceSpecification())), takeUntilDestroyed()).subscribe();
    }
    ngOnChanges(changes) {
      if (!this.sourceDirective.sourceId()) {
        return;
      }
      if (changes.url && !changes.url.isFirstChange() || changes.tiles && !changes.tiles.isFirstChange() || changes.bounds && !changes.bounds.isFirstChange() || changes.minzoom && !changes.minzoom.isFirstChange() || changes.maxzoom && !changes.maxzoom.isFirstChange() || changes.tileSize && !changes.tileSize.isFirstChange() || changes.scheme && !changes.scheme.isFirstChange() || changes.attribution && !changes.attribution.isFirstChange()) {
        this.sourceDirective.refresh();
      }
    }
    getRasterSourceSpecification() {
      return {
        type: 'raster',
        url: this.url(),
        tiles: this.tiles(),
        bounds: this.bounds(),
        minzoom: this.minzoom(),
        maxzoom: this.maxzoom(),
        tileSize: this.tileSize(),
        scheme: this.scheme(),
        attribution: this.attribution()
      };
    }
    static {
      this.ɵfac = function RasterSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RasterSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: RasterSourceComponent,
        selectors: [["mgl-raster-source"]],
        inputs: {
          url: [1, "url"],
          tiles: [1, "tiles"],
          bounds: [1, "bounds"],
          scheme: [1, "scheme"],
          minzoom: [1, "minzoom"],
          maxzoom: [1, "maxzoom"],
          tileSize: [1, "tileSize"],
          attribution: [1, "attribution"]
        },
        features: [i0.ɵɵHostDirectivesFeature([{
          directive: SourceDirective,
          inputs: ["id", "id"]
        }]), i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function RasterSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return RasterSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-vector-source` - a vector source component
 * @see [vector](https://maplibre.org/maplibre-style-spec/sources/#vector)
 *
 * @category Source Components
 */
let VectorSourceComponent = /*#__PURE__*/(() => {
  class VectorSourceComponent {
    constructor() {
      /** Init injections */
      this.sourceDirective = inject(SourceDirective);
      /** Dynamic inputs */
      this.url = input();
      this.tiles = input();
      this.bounds = input();
      this.scheme = input();
      this.minzoom = input();
      this.maxzoom = input();
      this.attribution = input();
      this.promoteId = input();
      this.sourceDirective.loadSource$.pipe(tap$1(() => this.sourceDirective.addSource(this.getVectorSourceSpecification())), takeUntilDestroyed()).subscribe();
    }
    ngOnChanges(changes) {
      if (!this.sourceDirective.sourceId()) {
        return;
      }
      if (changes.bounds && !changes.bounds.isFirstChange() || changes.scheme && !changes.scheme.isFirstChange() || changes.minzoom && !changes.minzoom.isFirstChange() || changes.maxzoom && !changes.maxzoom.isFirstChange() || changes.attribution && !changes.attribution.isFirstChange() || changes.promoteId && !changes.promoteId.isFirstChange()) {
        this.sourceDirective.refresh();
      } else if (changes.url && !changes.url.isFirstChange() || changes.tiles && !changes.tiles.isFirstChange()) {
        const source = this.sourceDirective.getSource();
        if (source === undefined) {
          return;
        }
        const url = this.url();
        if (changes.url && url) {
          source.setUrl(url);
        }
        const tiles = this.tiles();
        if (changes.tiles && tiles) {
          source.setTiles(tiles);
        }
      }
    }
    getVectorSourceSpecification() {
      return {
        type: 'vector',
        url: this.url(),
        tiles: this.tiles(),
        bounds: this.bounds(),
        scheme: this.scheme(),
        minzoom: this.minzoom(),
        maxzoom: this.maxzoom(),
        attribution: this.attribution(),
        promoteId: this.promoteId()
      };
    }
    static {
      this.ɵfac = function VectorSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VectorSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: VectorSourceComponent,
        selectors: [["mgl-vector-source"]],
        inputs: {
          url: [1, "url"],
          tiles: [1, "tiles"],
          bounds: [1, "bounds"],
          scheme: [1, "scheme"],
          minzoom: [1, "minzoom"],
          maxzoom: [1, "maxzoom"],
          attribution: [1, "attribution"],
          promoteId: [1, "promoteId"]
        },
        features: [i0.ɵɵHostDirectivesFeature([{
          directive: SourceDirective,
          inputs: ["id", "id"]
        }]), i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function VectorSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return VectorSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * `mgl-video-source` - a video source
 * @see [video](https://maplibre.org/maplibre-style-spec/sources/#video)
 *
 * @category Source Components
 */
let VideoSourceComponent = /*#__PURE__*/(() => {
  class VideoSourceComponent extends SourceDirective {
    constructor() {
      super();
      /** Dynamic input */
      this.urls = input.required();
      /** Dynamic input */
      this.coordinates = input.required();
      this.loadSource$.pipe(tap$1(() => this.addSource()), takeUntilDestroyed()).subscribe();
    }
    ngOnChanges(changes) {
      if (!this.sourceId()) {
        return;
      }
      if (changes.urls && !changes.urls.isFirstChange()) {
        this.refresh();
      } else if (changes.coordinates && !changes.coordinates.isFirstChange()) {
        const source = this.mapService.getSource(this.id());
        if (source === undefined) {
          return;
        }
        source.setCoordinates(changes.coordinates.currentValue);
      }
    }
    addSource() {
      const source = {
        type: 'video',
        urls: this.urls(),
        coordinates: this.coordinates()
      };
      this.mapService.addSource(this.id(), source);
      this.sourceId.set(this.id());
    }
    static {
      this.ɵfac = function VideoSourceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VideoSourceComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: VideoSourceComponent,
        selectors: [["mgl-video-source"]],
        inputs: {
          urls: [1, "urls"],
          coordinates: [1, "coordinates"]
        },
        features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
        decls: 0,
        vars: 0,
        template: function VideoSourceComponent_Template(rf, ctx) {},
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return VideoSourceComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const componentsAndDirectives = [MapComponent, LayerComponent, DraggableDirective, ImageComponent, VectorSourceComponent, GeoJSONSourceComponent, RasterDemSourceComponent, RasterSourceComponent, ImageSourceComponent, VideoSourceComponent, CanvasSourceComponent, FeatureComponent, MarkerComponent, PopupComponent, ControlComponent, FullscreenControlDirective, NavigationControlDirective, GeolocateControlDirective, AttributionControlDirective, ScaleControlDirective, PointDirective, ClusterPointDirective, MarkersForClustersComponent, TerrainControlDirective, SourceDirective];
let NgxMapLibreGLModule = /*#__PURE__*/(() => {
  class NgxMapLibreGLModule {
    static {
      this.ɵfac = function NgxMapLibreGLModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgxMapLibreGLModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxMapLibreGLModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return NgxMapLibreGLModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-maplibre-gl
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AttributionControlDirective, CanvasSourceComponent, ClusterPointDirective, ControlComponent, CustomControl, DraggableDirective, FeatureComponent, FullscreenControlDirective, GeoJSONSourceComponent, GeolocateControlDirective, ImageComponent, ImageSourceComponent, LayerComponent, MapComponent, MapService, MarkerComponent, MarkersForClustersComponent, NavigationControlDirective, NgxMapLibreGLModule, PointDirective, PopupComponent, RasterDemSourceComponent, RasterSourceComponent, ScaleControlDirective, SourceDirective, TerrainControlDirective, VectorSourceComponent, VideoSourceComponent };
